import { Card, Form, Button, Image } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify'
import { fetchWrapper } from '_helpers/fetch-wrapper';
import { getMeetingCardDisplayAssignment } from '../../_helpers/meetingCardDisplay';
import { notifyEditPermissionDenied } from 'utils/editPermissionToast';
import { isOfficer } from 'utils/acl';

export default function MeetingCard({ meeting, assignments = [], isLocked = false }) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(meeting.meetingDetails?.meetingTheme || '');
  const [loading, setLoading] = useState(false);
  const [displayRole, setDisplayRole] = useState(meeting.meetingDetails?.meetingCardDisplayRole || 'Toastmaster');
  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector(x => x.auth);

  // console.log('meeting in MeetingCard: ', meeting);

  useEffect(() => {
    setEditing(false);
    setInputValue(meeting.meetingDetails?.meetingTheme || '');
    setDisplayRole(meeting.meetingDetails?.meetingCardDisplayRole || 'Toastmaster');
  }, [meeting]);

  useEffect(() => {
    fetchWrapper.get(`${url}/meetingsConfig`)
      .then((config) => {
        if (!meeting.meetingDetails?.meetingCardDisplayRole && (config?.meetingCardDisplayRole || (Array.isArray(config?.meetingCardDisplayRoles) && config.meetingCardDisplayRoles.length > 0))) {
          setDisplayRole(config.meetingCardDisplayRole || config.meetingCardDisplayRoles[0]);
        }
      })
      .catch(() => setDisplayRole('Toastmaster'));
  }, [url, meeting.meetingDetails?.meetingCardDisplayRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authUser?.token || !isOfficer(authUser?.user?.acl || authUser?.acl)) {
      notifyEditPermissionDenied(authUser);
      return;
    }
    setLoading(true);
    try {
      const data = {
        meetingTheme: inputValue.trim() ? inputValue : ''
      };
      const response = await fetch(`${url}/meeting/${meeting.meetingDetails?.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authUser.token
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        const resData = await response.json();
        setInputValue(resData.meetingTheme || '');
        if (meeting && meeting.meetingDetails) {
          meeting.meetingDetails.meetingTheme = resData.meetingTheme || '';
        }
        setEditing(false);
      } else {
        toast.error(`Failed to update the meeting theme. Status: ${response.status}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error('An error occurred while updating the meeting theme', { position: 'bottom-left' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setInputValue(meeting.meetingDetails?.meetingTheme || '');
    setEditing(false);
  };

  const handleEditClick = () => {
    if (isLocked) return;
    if (!authUser?.token || !isOfficer(authUser?.user?.acl || authUser?.acl)) {
      notifyEditPermissionDenied(authUser);
      return;
    }
    setEditing(true);
    setInputValue(meeting.meetingDetails?.meetingTheme || '');
  };

  const meetingTitle = meeting.meetingDetails?.meetingTheme ? meeting.meetingDetails?.meetingTheme : '';
  const displayAssignment = getMeetingCardDisplayAssignment(assignments, displayRole);
  const displayRoleLabel = String(displayAssignment.roleLabel || 'Role').toUpperCase();

  return (
    <Card className="text-center meetings-mock-card-shell meeting-card-static meetingpage-top-meeting-card mt-3 mb-3" >
      <Card.Body className='meetings-mock-card-body'>
        <div className='meetings-mock-card'>
          <div className='meetings-mock-top'>
            <div className='meetings-mock-date-group'>
              <span className='meetings-mock-date'>
                {meeting.meetingDetails?.meetingDate || ''}
              </span>
              {meeting.meetingDetails?.meetingNumber && (
                <span className='meetings-mock-number'>
                  #{meeting.meetingDetails.meetingNumber}
                </span>
              )}
            </div>
            {isLocked && (
              <span className='meetings-mock-lock-badge' title='This meeting is locked'>
                <i className='bi bi-lock-fill' aria-hidden='true' />
              </span>
            )}
          </div>

          <div className='meetings-mock-avatar'>
            {displayAssignment.avatar
              ? <Image src={displayAssignment.avatar} alt='Assigned role avatar' />
              : displayAssignment.initials
                ? <span className='meetings-mock-avatar-fallback'>{displayAssignment.initials}</span>
                : <i className='bi bi-person-fill meetings-mock-avatar-fallback'></i>}
          </div>

          {editing ? (
            <Form onSubmit={handleSubmit} className='mt-2'>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  size="sm"
                  type="text"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  autoFocus
                  placeholder="Enter meeting theme"
                />
                {inputValue && (
                  <button
                    type="button"
                    aria-label="Clear"
                    onClick={() => setInputValue('')}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      margin: 0,
                      cursor: 'pointer',
                      fontSize: '1.1em',
                      color: 'var(--foreground)',
                      lineHeight: 1,
                      zIndex: 2
                    }}
                    tabIndex={-1}
                  >
                    ×
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 10 }}>
                <Button variant="success" size="sm" type="submit" disabled={loading}>
                  {loading ? <AppLoader size="sm" inline /> : 'Save'}
                </Button>
                <Button variant="secondary" size="sm" type="button" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </Form>
          ) : (
            <h3
              className='meetings-mock-title'
              style={{ cursor: isLocked ? 'default' : 'pointer' }}
              onClick={() => !editing && handleEditClick()}
              title={isLocked ? 'This meeting is locked' : meetingTitle ? 'Edit theme' : 'Add theme'}
            >
              {meetingTitle || '+ Add theme'}
            </h3>
          )}

          <div className='meetings-mock-separator'></div>
          <div className='meetings-mock-role-label'>{displayRoleLabel}:</div>
          <div className='meetings-mock-role-name'>{displayAssignment.displayName}</div>
        </div>
      </Card.Body>
    </Card>
  );
}
